import { Campaign } from "@common-models/campaign-model";
import { Reminder } from "@common/models/reminder";
import { isTempId } from "@common-reducers/DBServiceThunks";
import type { CommonRootState } from "@common/types/common-root-state-type";
import { selectUser } from "@common-reducers/UserSelectors";
import { isExtensionContext } from "@common-services/detect-context";
import { isValidChromeRuntime } from "@common-services/storage";
import { setWapiInitialized } from "@common/reducers/WhatsAppReducer";
import store, { dispatch } from "@extension-context-common/store/extension-store";
import { createAsyncThunk } from "@reduxjs/toolkit";
import moment from "moment";
import { nextCampaignMessageThunk, sendScheduleMessageThunk } from "../thunks/WhatsappSendMessageThunk";
import { waReloadPageTool } from "../thunks/WhatsappThunks";
import { selectNextSendable, type PacerMessage } from "@/bt-common/scheduling/send-pacer";
import { offerHolds, isOfferablePlausibly, isPastLocalExpiry, createSkipReporter, describeSkip, type OfferHolds, type PacerSkipReason, type SkippedMessage } from "./offer-holds";
import { createExtensionBackendActionsClient } from "@extension/wa-communication-layer/api/backend-actions-client.adapter";

import { NotifierConnectionStatus } from "@/common/noifier-module/types";
import { reconnectNotifierIfNeeded } from "@/common/noifier-module/NotifierThunks";
import { AnalyticsService } from "@common-services/analytics-service";
import { selectIsWhatsappInitialized } from "@common/reducers/ConnectionReadinessSlice";
import { openReminderDialog } from "@/common/reducers/RemindersUIReducer";
import { processExpiredHandleLaterThunk } from "@common-reducers/labels-reducer/labels-thunks";

const analyticsService = new AnalyticsService();
const campaignStatusMap = new Map<string, string>();

// Pacer diagnostics are opt-in (localStorage BT_PACER_DIAG=1) — the pacer
// ticks every ~5s forever, so unconditional logging floods the console.
const PACER_DIAG: boolean = (() => {
  try { return localStorage.getItem('BT_PACER_DIAG') === '1'; } catch { return false; }
})();
function pacerLog(msg: string): void {
  if (PACER_DIAG) console.log(msg);
}

/**
 * Pure helper: given a flat list of user messages, an inFlight set, and the
 * current epoch (ms), return up to 3 messages to OFFER to the server — ordered by
 * selectNextSendable (retryCount DESC, dueDate ASC).
 *
 * FE#447: this machine's clock does not decide whether a message is due or too late — the SERVER does,
 * on the clock that minted the dueDate (and says so with `not_yet_due` + `retryAfterMs`, or
 * `window_expired`). So every pending message is offered, except (a) one the server told us to hold off
 * on (see offer-holds.ts) and (b) one dated further ahead than any believable clock skew. `now` only feeds
 * that loose bound. Returns the ORIGINAL objects (the send reads its own fields).
 *
 * Exported for unit testing without any store/redux/alarm deps.
 */
export function orderPendingForSend(
  messages: any[],
  inFlight: ReadonlySet<string>,
  now: number,
  holds: OfferHolds = offerHolds,
  onSkip?: (message: SkippedMessage, reason: PacerSkipReason) => void,
): any[] {
  // Step 1: build the candidate list — only truthy, non-temp, non-deleted,
  // pending-or-undefined-status, not held by the server's last verdict.
  const candidates: PacerMessage[] = [];
  const demotedIds = new Set<string>(); // #591: last send hung recently → offered after fresh messages
  for (const message of messages) {
    if (!message) continue;
    if (isTempId(message.id ?? null)) continue;
    if (message.deleted) continue;
    const isPending = message.status === 'pending' || message.status === undefined;
    if (!isPending) continue;
    if (!isOfferablePlausibly(message, now)) {
      // H16: only an already-attempted message here is anomalous; a fresh far-future schedule is normal.
      if ((message.retryCount ?? 0) > 0 || message.firstSendAttempt) onSkip?.(message, 'implausible_due');
      continue;
    }
    if (isPastLocalExpiry(message, now)) { // backstop: stale messages never flood the server
      onSkip?.(message, 'local_expiry');
      continue;
    }
    if (holds.isHeld(message)) continue;
    if (holds.isRecentlyHung(message)) demotedIds.add(message._id);
    candidates.push({
      _id: message._id,
      dueDate: message.dueDate,
      status: message.status ?? 'pending',
      retryCount: message.retryCount,
    } as PacerMessage);
  }

  // Step 2: use selectNextSendable up to 3 times; each selected message is
  // removed from the working inFlight copy so the next iteration picks a
  // different one.
  const selected: any[] = [];
  const working = new Set(inFlight);
  const messageById = new Map<string, any>(messages.filter(Boolean).map((m) => [m._id, m]));

  // #591: fresh messages first; a recently hung one only fills the slots left over, so it cannot re-take the front.
  const fresh = candidates.filter((c) => !demotedIds.has(c._id));
  const demoted = candidates.filter((c) => demotedIds.has(c._id));

  for (let i = 0; i < 3; i++) {
    // Infinity: dueDate is NOT a gate here (the server judges it); the pacer only orders and dedups.
    const next = selectNextSendable(fresh, working, Number.POSITIVE_INFINITY)
      ?? selectNextSendable(demoted, working, Number.POSITIVE_INFINITY);
    if (!next) break;
    working.add(next._id);
    const original = messageById.get(next._id);
    if (original) selected.push(original);
  }

  pacerLog(`[PACER] orderPendingForSend: in=${messages.length} candidates=${candidates.length} inFlight=${inFlight.size} -> selected=${selected.length} [${selected.map((m) => `${m._id}(rc${m.retryCount ?? 0})`).join(', ')}]`);
  return selected;
}

// Helper: extension store state is a superset of CommonRootState at runtime,
// but the GenericState<T> reducer types fail strict structural overlap. Use
// a typed getter to centralize the boundary cast.
function getCommonState(): CommonRootState {
  return store.getState() as object as CommonRootState;
}

const errorCounter = {
  isWhatsappInitializedErrorCounter: 0,
  isSocketConnectedErrorCounter: 0,
}

export class SendMessagesInterval {
  private static instance: SendMessagesInterval;
  private alarmName = "send-messages-alarm";
  private isProcessing = false;
  private isListenerInitialized = false;
  private isIntervalClockInitialized = false;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private readonly minMessageInterval = 5; // Assuming it divides into days
  // Defensive only: within a cycle the dedup is done by orderPendingForSend's working set, and
  // cycles are serialized by `isProcessing`, so this never holds more than one id and is cleared
  // (finally) before the next dispatch. It is NOT cross-cycle protection — the try/finally
  // placement is what would make a future *concurrent* dispatch loop safe.
  private readonly inFlight = new Set<string>();
  // H16 (#534): one durable attempt row per message+reason when the pacer silently stops offering it.
  private readonly skipReporter = createSkipReporter((message, reason) => {
    void createExtensionBackendActionsClient()
      .createMessageAttemptLog({
        messageId: message._id,
        attemptType: (message.retryCount ?? 0) === 0 ? 'initial' : 'client-retry',
        whatsappStatus: 'unknown', // the pacer does not read WhatsApp state; matches the server's own default
        logMessage: describeSkip(reason),
      })
      .catch(() => undefined);
  });

  private constructor() { }

  public static getInstance(): SendMessagesInterval {
    if (!SendMessagesInterval.instance) {
      SendMessagesInterval.instance = new SendMessagesInterval();
    }
    return SendMessagesInterval.instance;
  }

  // Public only as a test seam. In production this must run behind the caller's `isProcessing`
  // gate (handleAlarmTrigger) — it does not guard itself against concurrent invocation.
  public async processAndSendScheduledMessages(): Promise<void> {
    const state = store.getState();
    const now = Date.now();

    const userMessageList = Object.values(state.DBUserMessageReducer.entities);
    pacerLog(`[PACER] processAndSend START @${new Date(now).toISOString()} storeMsgs=${userMessageList.length} inFlight=${this.inFlight.size}`);

    this.skipReporter.prune(new Set(userMessageList.filter(Boolean).map((m) => m!._id)));
    const toSend = orderPendingForSend(userMessageList, this.inFlight, now, offerHolds, (m, reason) => this.skipReporter.report(m, reason));
    pacerLog(`[PACER] processAndSend toSend=${toSend.length} [${toSend.map((m: any) => m._id).join(', ')}]`);

    for (let i = 0; i < toSend.length; i++) {
      const message = toSend[i];
      pacerLog(`[PACER] dispatch[${i}] id=${message._id} rc=${message.retryCount ?? 0} due=${message.dueDate} -> sendScheduleMessageThunk`);
      this.inFlight.add(message._id);
      try {
        await store.dispatch(sendScheduleMessageThunk({ userMessage: message }));
        pacerLog(`[PACER] dispatch[${i}] id=${message._id} resolved`);
      } finally {
        this.inFlight.delete(message._id);
      }
    }
  }

  public initializeAlarmListener(): void {
    // Prevent multiple listeners from being registered
    if (this.isListenerInitialized) {
      // console.log("[SendMessagesInterval] Alarm listener already initialized, skipping");
      return;
    }

    // Set up message listener for alarm events from background script
    // The alarm is auto-started by the background script, we just need to listen
    // console.log("[SendMessagesInterval] Initializing alarm listener");

    chrome.runtime.onMessage.addListener((message, sender, sendResponse) => {
      // console.log("[SendMessagesInterval] Received message", message);
      if (message.type === 'ALARM_TRIGGERED' && message.alarmName === this.alarmName) {
        pacerLog(`[PACER] alarm-msg received (${this.alarmName}) @${new Date().toISOString()}`);
        this.handleAlarmTrigger();
        sendResponse({ success: true });
      }
    });

    this.isListenerInitialized = true;
    pacerLog(`[PACER] alarm listener registered (alarm=${this.alarmName})`);
  }

  /**
   * Regular-timeout clock — the PRIMARY, fast (5s) cadence while the WhatsApp
   * tab is visible. chrome.alarms (initializeAlarmListener) remains the FALLBACK
   * for when the tab is hidden/asleep: page timers get throttled there, and MV3
   * clamps the alarm to a ~30s minimum period. The two are mutually exclusive by
   * visibility (interval runs only when visible) to avoid redundant ticking;
   * handleAlarmTrigger's `isProcessing` guard is the safety net across visibility
   * flips. BOTH clocks funnel through handleAlarmTrigger, so the pacer
   * (orderPendingForSend) governs every send regardless of which clock fired.
   */
  public startSendMessagesInterval(): void {
    if (this.isIntervalClockInitialized) {
      return;
    }
    this.isIntervalClockInitialized = true;

    const applyVisibility = (): void => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') {
        this.startInterval();
      } else {
        this.stopInterval();
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', applyVisibility);
    }
    applyVisibility(); // set the initial state immediately
  }

  private startInterval(): void {
    if (this.intervalId) {
      return;
    }
    pacerLog('[PACER] regular-timeout clock STARTED (5s setInterval, tab visible)');
    this.intervalId = setInterval(() => {
      pacerLog(`[PACER] timer tick (5s setInterval) @${new Date().toISOString()}`);
      void this.handleAlarmTrigger();
    }, this.minMessageInterval * 1000);
  }

  private stopInterval(): void {
    if (!this.intervalId) {
      return;
    }
    clearInterval(this.intervalId);
    this.intervalId = null;
    pacerLog('[PACER] regular-timeout clock STOPPED (tab hidden -> chrome.alarms fallback)');
  }

  private async handleAlarmTrigger(): Promise<void> {
    if (this.isProcessing) {
      pacerLog('[PACER] tick SKIP — already processing (prev cycle still running)');
      return;
    }

    const state = getCommonState();
    const user = selectUser(state);

    if (!user || !isValidChromeRuntime() || !isExtensionContext()) {
      pacerLog(`[PACER] tick BAIL — gate1 user=${!!user} validRuntime=${isValidChromeRuntime()} extCtx=${isExtensionContext()}`);
      return;
    }

    const settings = state?.MainReducer?.settings;
    const isWhatsappInitialized = selectIsWhatsappInitialized(state);

    if (!isWhatsappInitialized) {
      pacerLog(`[PACER] tick BAIL — whatsapp NOT initialized (count=${errorCounter.isWhatsappInitializedErrorCounter + 1})`);
      // console.log("[SendMessagesInterval] whatsapp not initialized");
      errorCounter.isWhatsappInitializedErrorCounter++;
      if (errorCounter.isWhatsappInitializedErrorCounter > 15) {
        if (settings?.isWhatsappInitializedReloadEnabled === true) {
          // console.log("[SendMessagesInterval] whatsapp not initialized, reloading page");
          await waReloadPageTool("whatsapp not initialized");
        }
        return;
      }
      return;
    }
    errorCounter.isWhatsappInitializedErrorCounter = 0;

    const isSocketConnected = state.NotifierReducer?.connectionStatus === NotifierConnectionStatus.AUTHENTICATED

    if (!isSocketConnected) {
      pacerLog(`[PACER] tick BAIL — notifier socket NOT authenticated (status=${state.NotifierReducer?.connectionStatus})`);
      console.log("[SendMessagesInterval] notification service is not connected, attempting reconnection...");
      errorCounter.isSocketConnectedErrorCounter++;

      // Try to reconnect the MQTT socket
      try {
        const reconnected = await dispatch(reconnectNotifierIfNeeded()).unwrap();
        if (reconnected) {
          console.log("[SendMessagesInterval] MQTT reconnection initiated");
        } else {
          console.log("[SendMessagesInterval] MQTT reconnection not needed or no token available");
        }
      } catch (error) {
        console.error("[SendMessagesInterval] MQTT reconnection failed:", error);
      }

      // Still return early - next alarm cycle will check if reconnection succeeded
      return;
    }
    errorCounter.isSocketConnectedErrorCounter = 0;

    pacerLog('[PACER] tick PASS gates -> processing');
    this.isProcessing = true;
    try {
      await this.processAndSendScheduledMessages();
      await this.processNextMessageForPendingAndActiveCampaigns();
      await this.processActiveReminders();
      await this.processExpiredHandleLater();
    } catch (e) {
      console.error("[SendMessagesInterval] error", e);
    } finally {
      this.isProcessing = false;
    }
  }

  // Alarm runs automatically - no manual stop needed

  private async processNextMessageForPendingAndActiveCampaigns(): Promise<void> {
    const state = store.getState()
    const campaigns = Object.values(state.DBCampaignReducer.entities) as Campaign[];

    const now = moment.utc();
    const pendingOrActiveCampaigns = campaigns.filter(
      (campaign) => {
        // Don't request next message if campaign is complete-sent or complete-delivered
        if (campaign.campaignStatus === "complete-sent" || campaign.campaignStatus === "complete-delivered") {
          return false;
        }
        return campaign.campaignStatus === "running" ||
          (campaign.campaignStatus === "pending" &&
            (!campaign.schedule?.dueDate || moment.utc(campaign.schedule.dueDate).isBefore(now)))
      }
    );

    for (const campaign of pendingOrActiveCampaigns) {
      if (campaign._id && !isTempId(campaign._id)) {
        await dispatch(nextCampaignMessageThunk(campaign._id));
      }
    }
  }

  private async processActiveReminders(): Promise<void> {
    try {
      const state = store.getState()
      const reminders = Object.values(state.DBRemindersReducer.entities) as Reminder[];

      const now = moment.utc();
      const activeReminders = reminders.filter(
        (reminder) => {
          return reminder.isActive && reminder.dueAt && moment.utc(reminder.dueAt).isBefore(now);
        }
      );

      for (const reminder of activeReminders) {
        if (reminder._id && !isTempId(reminder._id)) {
          await store.dispatch(openReminderDialog(reminder));
        }
      }
    } catch (e) {
      console.error("[processActiveReminders] error", e);
    }
  }

  private async processExpiredHandleLater(): Promise<void> {
    try {
      await (store.dispatch as any)(processExpiredHandleLaterThunk());
    } catch (e) {
      console.error("[processExpiredHandleLater] error", e);
    }
  }
}

export const initSendMessagesInterval = createAsyncThunk<void, void, { state: CommonRootState }>("initSendMessagesInterval",
  async (_, thunkApi) => {

    if (!window?.location?.href?.includes("web.whatsapp.com")) {
      return;
    }

    const sendMessagesInterval = SendMessagesInterval.getInstance();
    sendMessagesInterval.initializeAlarmListener();   // chrome.alarms — sleep fallback
    sendMessagesInterval.startSendMessagesInterval(); // setInterval — focused-tab primary
  }
);

export const onWapiInitialized = createAsyncThunk<void, void, { state: CommonRootState }>("onWapiInitialized", async (_, thunkApi) => {
  const dispatch = thunkApi.dispatch;
  dispatch(setWapiInitialized(true))
  dispatch(initSendMessagesInterval())
});


/*
export const wakeupFromBackground = createAsyncThunk("wakeupFromBackground", async (_, thunkApi) => {
  const dispatch = thunkApi.dispatch;
  const state = thunkApi.getState() as CommonRootState;

  const settings = state?.MainReducer?.settings;

  dispatch(setInitialized(true))
  ContentMessageServiceActions.getMyWaProfile();
  
  const res = await ContentMessageServiceActions.getUserInfo();
  console.log(`User info result: ${JSON.stringify(res)}`);

  ContentMessageServiceActions.getChatList();


  if (!settings?.isBackgroundIntervalEnabled) {
      console.log("isBackgroundEnabled", settings.isBackgroundIntervalEnabled)
      dispatch(initSendMessagesInterval())
  }


  //dispatch(initMessageSentConfirmation())

  // dispatch(getCurrentChatThunk({}));
});
*/
